import { Box, Divider, SelectChangeEvent } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import BriaDropDown, { DropDownItem } from "../../../../../components/common/BriaDropDown/BriaDropDown";
import StaticDropdown, { AspectRatio } from "../../../../../components/common/DropDowns/StaticDropdown";
import useStaticDropdown from "../../../../../components/common/DropDowns/useStaticDropdown";
import { APPS } from "../../../../../constants/AppsConstants";
import LinkConstants from "../../../../../constants/LinkConstants";
import { useAppStore } from "../../../../../hooks/useStores";
import InputLayout from "../../../../../layout/InputLayout/InputLayout";
import { ImageEditingViewMode } from "../../../../../models/image-to-image";
import { FPS } from "../../../../../models/video-editing";
import NewTgDropdown from "../../../Models/Viewer/Playground/Dropdown/NewTgDropdown";
import { TgModel } from "../../../Models/store/tg-model-store";
import VideoEditingInputFrames from "./InputFrames/VideoEditingInputFrames";
import TgPromptInput from "./PromptInput/TgPromptInput";
import styles from "./VideoEditingConfig.module.scss";

const VideoEditingConfig = () => {
	const { t } = useTranslation("translation", { keyPrefix: "videoEditing.config" });
	const { videoEditingStore, playgroundStore } = useAppStore();
	const { items: fpsOptions } = useStaticDropdown<FPS>({ tKeyPrefix: "videoEditing.config.fps.options" });
	const { config, handleConfigChange } = videoEditingStore;
	const [selectedModel, setSelectedModel] = useState<TgModel>();
	const disablePrompt = !config.model_id;

	useEffect(() => {
		playgroundStore.handleAppChange(APPS.TG_VIDEO_EDITING);
		playgroundStore.imageEditingViewMode = ImageEditingViewMode.SINGLE;
	}, []);

	const handleModelChange = (_e: SelectChangeEvent<string>, selectedItem: DropDownItem<string, TgModel>) => {
		setSelectedModel(selectedItem.extraData);
		handleConfigChange("model_id", selectedItem.value);
	};

	return (
		<>
			<Box className={styles.config}>
				<Box className={styles.inputs}>
					<InputLayout label={t("models.label")} docsLink={LinkConstants.TG.TRAIN_MODEL.DOCS}>
						<NewTgDropdown
							value={videoEditingStore.config.model_id as string}
							onChange={handleModelChange}
						/>
					</InputLayout>
					<InputLayout label={t("aspectRatio.label")}>
						<StaticDropdown
							type="aspectRatio"
							value={config.aspect_ratio}
							onChange={(e) => handleConfigChange("aspect_ratio", e.target.value as AspectRatio)}
							disabled
						/>
					</InputLayout>
					<InputLayout label={t("fps.label")}>
						<BriaDropDown<FPS>
							items={fpsOptions.map((option) => ({ ...option, value: Number(option.value) as FPS }))}
							value={config.fps}
							onChange={(e) => handleConfigChange("fps", e.target.value as FPS)}
							disabled
						/>
					</InputLayout>
					<Divider className={styles.divider} />
					<InputLayout
						label={t("inputFrames.label")}
						docsLink={LinkConstants.VIDEO_EDITING.REMOVE_BG.DOCS}
						optional
					>
						<VideoEditingInputFrames />
					</InputLayout>
					<Divider className={styles.divider} />
					<InputLayout label={t("prompt.label")}>
						<TgPromptInput
							prefix={selectedModel?.generation_prefix}
							prompt={config.prompt}
							placeholder={t("prompt.placeholder")}
							onChange={(newPrompt) => handleConfigChange("prompt", newPrompt)}
							onEnter={() => videoEditingStore.generateVideo(config)}
							disabled={disablePrompt}
						/>
					</InputLayout>
				</Box>
				<BriaButton
					className={styles.primaryBtn}
					buttonType="primaryMedium"
					loading={videoEditingStore.loadingGenerate}
				>
					{t("primaryBtn")}
				</BriaButton>
			</Box>
		</>
	);
};

export default observer(VideoEditingConfig);
